import { memo } from 'react';

import NumberFormat from 'react-number-format';
import { useTheme } from 'styled-components';

import { Currencies } from '@constants/constants';
import { getCurrencySymbol } from '@utils/utils';
import { useBreakpoint, useDevice } from '@contexts/device';
import Accordion from '@components/Accordion/next/Accordion';
import Typography from '@components/Typography';

import {
  NoneCollapsableStyledPriceRangeFilter,
  PriceRangeButton,
  PriceRangeInputBox,
  PriceRangeInputLabel,
  PriceRangeInputWrapper,
  PriceRangeWrapper,
  StyledPriceRangeFilter,
} from './PriceRangeFilterStyle';

export interface PriceRangeFilterProps {
  minValue: number | undefined;
  maxValue: number | undefined;
  setMinValue: (val: number | undefined) => void;
  setMaxValue: (val: number | undefined) => void;
  handleSearch?: (a: number | undefined, b: number | undefined) => void;
  title: string;
  collapsable?: boolean;
}

const PriceRangeFilter = ({
  title,
  minValue,
  maxValue,
  setMinValue,
  setMaxValue,
  handleSearch,
  collapsable = true,
}: PriceRangeFilterProps) => {
  const { isDesktop } = useDevice();
  const { xl } = useBreakpoint();
  const { palette } = useTheme();
  const handleSearchClicked = (): void => {
    handleSearch?.(minValue, maxValue);
  };

  return collapsable ? (
    <StyledPriceRangeFilter>
      <Accordion
        label={title}
        headerContentColor='eclipse'
        backgroundColor='white'
        alwaysShowBottomBorder={false}
        headerPadding={isDesktop ? '0px' : '15px'}
        mobileLabelFontSize='16px'
        iconSize={16}
        iconColor='NL800'
        labelFontSize='16px'
        contentPadding='0px'
        defaultOpen={true}
        borderRadius={xl ? '8px' : '0px'}
        accordionTitleStyles={{
          color: palette.NL800,
        }}
      >
        <PriceRangeWrapper>
          <PriceRangeInputWrapper>
            {minValue ? (
              <PriceRangeInputLabel>
                <Typography variant='caption3' weight='Bold' color='D400'>
                  Min Fiyat
                </Typography>
              </PriceRangeInputLabel>
            ) : null}

            <PriceRangeInputBox>
              <NumberFormat
                allowLeadingZeros={false}
                allowNegative={false}
                type='text'
                className='listing-filters-accommodation-input'
                value={minValue || ''}
                onValueChange={(values) => {
                  setMinValue(values.floatValue);
                }}
                maxLength={14}
                suffix={' ' + getCurrencySymbol(Currencies.TL)}
                thousandSeparator='.'
                decimalSeparator=','
                placeholder='Min Fiyat'
                size={14}
                style={{
                  color: palette.NL500,
                  fontSize: '16px',
                  fontWeight: '400',
                }}
              />
            </PriceRangeInputBox>
          </PriceRangeInputWrapper>

          <PriceRangeInputWrapper>
            {maxValue ? (
              <PriceRangeInputLabel>
                <Typography variant='caption3' weight='Bold' color='D400'>
                  Max Fiyat
                </Typography>
              </PriceRangeInputLabel>
            ) : null}

            <PriceRangeInputBox>
              <NumberFormat
                allowLeadingZeros={false}
                allowNegative={false}
                type='text'
                className='listing-filters-accommodation-input'
                value={maxValue || ''}
                onValueChange={(values) => {
                  setMaxValue(values.floatValue);
                }}
                maxLength={14}
                thousandSeparator='.'
                decimalSeparator=','
                suffix={' ' + getCurrencySymbol(Currencies.TL)}
                placeholder='Max Fiyat'
                size={14}
                style={{
                  color: palette.NL500,
                  fontSize: '16px',
                  fontWeight: '400',
                }}
              />
            </PriceRangeInputBox>
          </PriceRangeInputWrapper>
        </PriceRangeWrapper>

        {isDesktop ? (
          <PriceRangeButton
            onClick={handleSearchClicked}
            disabled={!minValue && !maxValue}
          >
            Ara
          </PriceRangeButton>
        ) : null}
      </Accordion>
    </StyledPriceRangeFilter>
  ) : (
    <NoneCollapsableStyledPriceRangeFilter>
      <Typography variant='caption1' color='NL800' weight='Bold'>
        {' '}
        {title}
      </Typography>
      <PriceRangeWrapper>
        <PriceRangeInputWrapper>
          {minValue ? (
            <PriceRangeInputLabel>
              <Typography variant='caption3' weight='Regular' color='D400'>
                Min Fiyat
              </Typography>
            </PriceRangeInputLabel>
          ) : null}

          <PriceRangeInputBox>
            <NumberFormat
              allowLeadingZeros={false}
              allowNegative={false}
              type='text'
              className='listing-filters-accommodation-input'
              value={minValue || ''}
              onValueChange={(values) => {
                setMinValue(values.floatValue);
              }}
              maxLength={14}
              suffix={' ' + getCurrencySymbol(Currencies.TL)}
              thousandSeparator='.'
              decimalSeparator=','
              placeholder='Min Fiyat'
              size={14}
              style={{
                color: palette.NL500,
                fontSize: '16px',
                fontWeight: '600',
              }}
            />
          </PriceRangeInputBox>
        </PriceRangeInputWrapper>

        <PriceRangeInputWrapper>
          {maxValue ? (
            <PriceRangeInputLabel>
              <Typography variant='caption3' weight='Regular' color='D400'>
                Max Fiyat
              </Typography>
            </PriceRangeInputLabel>
          ) : null}

          <PriceRangeInputBox>
            <NumberFormat
              allowLeadingZeros={false}
              allowNegative={false}
              type='text'
              className='listing-filters-accommodation-input'
              value={maxValue || ''}
              onValueChange={(values) => {
                setMaxValue(values.floatValue);
              }}
              maxLength={14}
              thousandSeparator='.'
              decimalSeparator=','
              suffix={' ' + getCurrencySymbol(Currencies.TL)}
              placeholder='Max Fiyat'
              size={14}
              style={{
                color: palette.NL500,
                fontSize: '16px',
                fontWeight: '600',
              }}
            />
          </PriceRangeInputBox>
        </PriceRangeInputWrapper>
      </PriceRangeWrapper>

      {isDesktop ? (
        <PriceRangeButton
          onClick={handleSearchClicked}
          disabled={!minValue && !maxValue}
        >
          Ara
        </PriceRangeButton>
      ) : null}
    </NoneCollapsableStyledPriceRangeFilter>
  );
};

export default memo(PriceRangeFilter);
